import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PostJobForm from '../../post-job/components/PostJobForm';
import { closeJob, completeJobAsEmployer, updateJob } from '../../../services/api';
import CardIcon from '../../../shared/components/icons/CardIcon';

const statusStyles = {
  pending: 'bg-orange-100 text-orange-700 border-orange-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-100 text-slate-700 border-slate-200',
};

function ClientJobCard({ job, onJobChanged }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const menuRef = useRef(null);
  const status = job.status?.toLowerCase() || 'pending';
  const statusClass = statusStyles[status] || statusStyles.pending;
  const location = [job.city, job.area].filter(Boolean).join(', ');
  const skillName = job.skill?.name || job.category || 'Unknown';
  const postedAt = job.postedAt?.full || job.postedAt?.date || 'Unknown date';
  const expiryValue = job.expiresAtCAT?.full || job.expiresAtCAT?.date || job.expiresAt;
  const budgetText = job.budget ? `K${job.budget}` : 'No budget proposed';
  const budgetClass = job.budget ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200';
  const applicants = job.applicants || [];
  const stats = job.statistics || {
    totalApplicants: applicants.length,
    pendingCount: applicants.filter((a) => a.applicationStatus === 'pending').length,
    hiredCount: applicants.filter((a) => a.applicationStatus === 'hired').length,
    rejectedCount: applicants.filter((a) => a.applicationStatus === 'rejected').length,
  };

  const applicantCount = stats.totalApplicants || 0;
  const requiredWorkers = Number(job.requiredPeople || 1);
  const hiredCount = Number(stats.hiredCount || 0);
  const slotsRemaining = Number(job.slotsRemaining ?? Math.max(requiredWorkers - hiredCount, 0));
  const progress = Math.min(100, Math.round((hiredCount / requiredWorkers) * 100));
  const canManage = status === 'pending' || status === 'active';

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpen]);

  const formatDisplayDate = (value) => {
    if (!value) return 'Unknown date';
    const parsed = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:,\s*(\d{2}:\d{2}))?/);
    if (parsed) return `${parsed[1]} ${new Date(Number(parsed[3]), Number(parsed[2]) - 1, Number(parsed[1])).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
    const dateValue = new Date(value);
    return Number.isNaN(dateValue.getTime()) ? value : dateValue.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatExpiry = (value) => {
    if (!value) return 'No expiry date';
    const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2})/);
    if (!match) return `Expires ${formatDisplayDate(value)}`;
    const expiryDate = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]), Number(match[4]), Number(match[5]));
    const today = new Date();
    const sameDay = expiryDate.toDateString() === today.toDateString();
    const label = sameDay ? 'today' : expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `Expires ${label} • ${match[4]}:${match[5]}`;
  };

  const handleEdit = async (formData) => {
    setIsSaving(true);
    try {
      await updateJob(job.id, { ...formData, status: job.status || 'pending' });
      toast.success('Job updated successfully.');
      setEditing(false);
      await onJobChanged?.();
    } catch (error) {
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async () => {
    setIsSaving(true);
    try {
      await closeJob(job.id);
      toast.success('Job closed successfully.');
      setCloseConfirmOpen(false);
      await onJobChanged?.();
    } catch (error) {
      toast.error(error.message || 'Unable to close job.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = async () => {
    setIsSaving(true);
    try {
      await completeJobAsEmployer(job.id);
      toast.success('Job marked as complete successfully.');
      setCompleteConfirmOpen(false);
      await onJobChanged?.();
    } catch (error) {
      toast.error(error.message || 'Unable to complete job.');
    } finally {
      setIsSaving(false);
    }
  };

  const initialData = {
    title: job.title || '',
    description: job.description || '',
    category: job.skill?.category?.slug || job.skill?.category?.id || '',
    categoryName: job.skill?.category?.name || '',
    skillId: job.skill?.id || '',
    city: job.city || '',
    area: job.area || '',
    budget: job.budget ?? '',
    requiredPeople: job.requiredPeople || 1,
    workType: job.workType || 'PART_TIME',
    isNegotiable: job.isNegotiable ?? true,
    urgency: job.urgency || 'normal',
  };

  return (
    <div className="relative rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-3 pr-12">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold uppercase text-navy">{job.title || 'Untitled job'}</h2>
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClass}`}>● {status.toUpperCase()}</span>
            </div>
            <p className="text-lg font-semibold text-slate-800">{skillName}</p>
            <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              <div className="inline-flex items-center gap-1.5"><CardIcon name="location" /><span className="font-semibold text-slate-900">{location || 'Location unavailable'}</span></div>
              <div className="inline-flex items-center gap-1.5"><CardIcon name="money" /><span className={`rounded-full border px-2 py-1 font-semibold ${budgetClass}`}>{budgetText}</span></div>
            </div>
          </div>

          {canManage && (
            <div ref={menuRef} className="absolute right-4 top-4">
              <button
                type="button"
                aria-label={`Actions for ${job.title || 'job'}`}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-2xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-300"
              >
                <span aria-hidden="true">⋮</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-11 z-20 w-36 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl">
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setEditing(true); }}
                    className="block w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Edit Job
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setCompleteConfirmOpen(true); }}
                    disabled={isSaving || status !== 'active'}
                    className="block w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    Complete Job
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setCloseConfirmOpen(true); }}
                    disabled={isSaving}
                    className="block w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    Close Job
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-5 border-t border-slate-100 pt-5">
          <div className="grid grid-cols-2 gap-3 text-sm text-slate-700 sm:grid-cols-4">
            <div><p className="text-xs text-slate-500">Applicants</p><p className="mt-1 text-lg font-bold text-navy">{applicantCount}</p></div>
            <div><p className="text-xs text-slate-500">Pending</p><p className="mt-1 text-lg font-bold text-navy">{stats.pendingCount ?? 0}</p></div>
            <div><p className="text-xs text-slate-500">Shortlisted</p><p className="mt-1 text-lg font-bold text-navy">{stats.shortlistedCount ?? 0}</p></div>
            <div><p className="text-xs text-slate-500">Hired</p><p className="mt-1 text-lg font-bold text-navy">{hiredCount}/{requiredWorkers}</p></div>
          </div>
          <div>
            <div className="flex items-center justify-between text-sm"><span className="font-semibold text-navy">Hiring progress</span><span className="text-slate-500">{hiredCount}/{requiredWorkers}</span></div>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} /></div>
            <p className="mt-2 text-sm text-slate-600">{slotsRemaining > 0 ? `${slotsRemaining} worker${slotsRemaining === 1 ? '' : 's'} still needed` : 'All workers hired'}</p>
          </div>
          <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
            <div>Posted <span className="font-semibold text-slate-900">{formatDisplayDate(postedAt)}</span></div>
            <div className={`inline-flex items-center gap-1.5 ${formatExpiry(expiryValue).includes('today') ? 'font-semibold text-amber-700' : ''}`}><CardIcon name="warning" />{formatExpiry(expiryValue)}</div>
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-slate-900/50 px-4 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="mb-4 flex items-center justify-between rounded-2xl bg-white px-6 py-4 shadow-xl">
              <h2 className="text-xl font-semibold text-navy">Edit job</h2>
              <button type="button" aria-label="Close edit form" onClick={() => setEditing(false)} className="text-2xl text-slate-500 hover:text-slate-900">×</button>
            </div>
            <PostJobForm
              initialData={initialData}
              onSubmit={handleEdit}
              isSubmitting={isSaving}
              submitLabel="Save changes"
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}

      {completeConfirmOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4 py-6">
          <div role="dialog" aria-modal="true" aria-labelledby={`complete-job-title-${job.id}`} className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">✓</div>
            <h2 id={`complete-job-title-${job.id}`} className="mt-4 text-xl font-semibold text-navy">Complete this job?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Mark <span className="font-semibold text-slate-900">{job.title || 'this job'}</span> as complete after the hired workers have finished their work.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setCompleteConfirmOpen(false)} disabled={isSaving} className="rounded-2xl border-2 border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">Keep active</button>
              <button type="button" onClick={handleComplete} disabled={isSaving} className="rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">{isSaving ? 'Completing...' : 'Complete job'}</button>
            </div>
          </div>
        </div>
      )}

      {closeConfirmOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 px-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`close-job-title-${job.id}`}
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <svg
                aria-hidden="true"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.3 3.8 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
              </svg>
            </div>
            <h2 id={`close-job-title-${job.id}`} className="mt-4 text-xl font-semibold text-navy">Close this job?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Closing <span className="font-semibold text-slate-900">{job.title || 'this job'}</span> will stop new applications. You can still review its existing applicants.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setCloseConfirmOpen(false)}
                disabled={isSaving}
                className="rounded-2xl border-2 border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep open
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="rounded-2xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? 'Closing...' : 'Close job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientJobCard;
